import { Box, Stack, Typography } from '@mui/material';
import ToolsIcon from 'components/Icons/sidebarIcons/toolsIcon';
import CheckIcon from '@mui/icons-material/Check';
import { TemplateTypeCnt } from './style';
import CorrectiveIcon from 'components/Icons/sidebarIcons/correctiveIcon';
import { useMemo } from 'react';

interface TemplateTypeBoxProps {
  index: number;
  type: 'CORRECTIVE_TASK' | 'WORK_ORDER';
  icon: React.ReactNode;
  title: string;
  description: string;
  selectedType: string;
  onSelect: (type: 'CORRECTIVE_TASK' | 'WORK_ORDER') => void;
}

const TemplateTypeBox = ({
  index,
  type,
  icon,
  title,
  description,
  selectedType,
  onSelect,
}: TemplateTypeBoxProps) => {
  const isSelected = selectedType === type;

  return (
    <Box
      className={`templateTypeBox`}
      onClick={() => onSelect(type)}
      sx={{
        ...(index === 0 && {
          marginBottom: '10px',
        }),
        ...(isSelected && {
          border: '1px solid rgba(104, 104, 254, 1) !important',
          backgroundColor: 'rgba(235, 234, 255, 1)',
        }),
      }}
    >
      <Box
        className="iconCnt"
        sx={{
          '& svg': {
            fontSize: 19,
          },
          ...(isSelected && {
            backgroundColor: 'rgba(104, 104, 254, 1) !important',
            '& svg': {
              fontSize: 19,
              '& path': {
                fill: 'none',
                stroke: 'white',
              },
            },
          }),
        }}
      >
        {icon}
      </Box>
      <Stack direction="column" gap={1}>
        <Typography
          className="typeTitle"
          sx={{
            ...(isSelected && {
              color: 'rgba(78, 72, 250, 1) !important',
            }),
          }}
        >
          {title}
        </Typography>
        <Typography className="typeSubTitle">{description}</Typography>
      </Stack>
      {isSelected && <CheckIcon className="checkIcon" />}
    </Box>
  );
};

// Main container component
const TemplateTypeContainer = ({ data, setData }) => {
  const handleTypeSelect = (templateType: 'CORRECTIVE_TASK' | 'WORK_ORDER') => {
    setData((prevData) => ({
      ...prevData,
      templateType,
    }));
  };

  const templateTypes = [
    {
      type: 'CORRECTIVE_TASK',
      icon: <CorrectiveIcon />,
      title: 'Corrective Task',
      description:
        'General assignments to track and complete daily operations.',
    },
    {
      type: 'WORK_ORDER',
      icon: <ToolsIcon />,
      title: 'Work Order',
      description: 'For maintenance, repairs, or service work.',
      className: 'mt-10', // Add margin top for second box
    },
  ] as const;

  const type = useMemo(() => {
    return data?.templateType ?? 'CORRECTIVE_TASK';
  }, [data?.templateType]);

  return (
    <TemplateTypeCnt>
      <Typography className="title">Template Type</Typography>
      <Typography className="subTitle">
        Choose whether this template will create a <b>Corrective Task</b> or a
        <b> Work Order</b>.
      </Typography>

      {templateTypes.map((template, index) => (
        <TemplateTypeBox
          index={index}
          key={template.type}
          type={template.type}
          icon={template.icon}
          title={template.title}
          description={template.description}
          selectedType={type}
          onSelect={handleTypeSelect}
        />
      ))}
    </TemplateTypeCnt>
  );
};

export default TemplateTypeContainer;
